import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, Eye, Database, Mail, Phone, MapPin } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: January 11, 2026</p>
          </div>

          <Card>
            <CardContent className="p-8 prose prose-gray dark:prose-invert max-w-none">
              <section className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Eye className="w-6 h-6 text-emerald-500" />
                    1. Information We Collect
                  </h2>
                  <p className="text-muted-foreground mt-4">
                    Visual Institute of Arts ("VIA", "we", "our", or "us") collects information to provide better services to our students. We collect:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li><strong>Personal Information:</strong> Name, email address, phone number, date of birth, and address when you register for an account or enroll in a course.</li>
                    <li><strong>Payment Information:</strong> Billing details, transaction history. We use secure payment gateways and do not store complete credit card numbers.</li>
                    <li><strong>Educational Records:</strong> Course enrollments, progress, assignment submissions, grades, and certificates.</li>
                    <li><strong>Communication Data:</strong> Messages with mentors, support tickets, and feedback.</li>
                    <li><strong>Device Information:</strong> IP address, browser type, device type for security and analytics purposes.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6 text-emerald-500" />
                    2. How We Use Your Information
                  </h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>To provide and maintain our educational services</li>
                    <li>To process course enrollments and payments</li>
                    <li>To communicate with you about your courses, assignments, and account</li>
                    <li>To assign mentors and facilitate student-mentor interactions</li>
                    <li>To issue certificates upon course completion</li>
                    <li>To improve our platform, courses, and services</li>
                    <li>To send promotional communications (with your consent)</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Lock className="w-6 h-6 text-emerald-500" />
                    3. Data Security
                  </h2>
                  <p className="text-muted-foreground mt-4">
                    We implement industry-standard security measures to protect your personal information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>SSL/TLS encryption for all data transmission</li>
                    <li>Secure password hashing using bcrypt</li>
                    <li>Regular security audits and vulnerability assessments</li>
                    <li>Access controls and authentication for all systems</li>
                    <li>Encrypted database storage</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">4. Data Sharing</h2>
                  <p className="text-muted-foreground mt-4">
                    We do not sell your personal information. We may share data with:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li><strong>Payment Processors:</strong> Razorpay for processing transactions securely</li>
                    <li><strong>Cloud Services:</strong> For hosting and infrastructure (data centers in India)</li>
                    <li><strong>Mentors/Instructors:</strong> Limited information necessary for teaching</li>
                    <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">5. Your Rights</h2>
                  <p className="text-muted-foreground mt-4">
                    Under applicable data protection laws, you have the right to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Request deletion of your data (subject to legal retention requirements)</li>
                    <li>Withdraw consent for marketing communications</li>
                    <li>Request data portability</li>
                    <li>Lodge a complaint with supervisory authorities</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">6. Cookies</h2>
                  <p className="text-muted-foreground mt-4">
                    We use cookies and similar technologies for:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>Authentication and session management</li>
                    <li>Remembering your preferences</li>
                    <li>Analytics to improve our services</li>
                  </ul>
                  <p className="text-muted-foreground mt-4">
                    You can control cookies through your browser settings.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">7. Data Retention</h2>
                  <p className="text-muted-foreground mt-4">
                    We retain your personal data for as long as your account is active or as needed to provide services. Educational records may be retained for certification verification purposes. Financial records are retained as required by Indian tax laws (typically 7 years).
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">8. Children's Privacy</h2>
                  <p className="text-muted-foreground mt-4">
                    Our services are intended for individuals 16 years and older. For students under 18, we require parental/guardian consent. Parent/guardian information is collected during enrollment.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">9. Contact Us</h2>
                  <div className="mt-4 space-y-3">
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-5 h-5 text-emerald-500" />
                      privacy@visualinstituteofarts.com
                    </p>
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-5 h-5 text-emerald-500" />
                      +91 98765 43210
                    </p>
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-5 h-5 text-emerald-500" />
                      Visual Institute of Arts, Mumbai, Maharashtra, India
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6 mt-8">
                  <p className="text-sm text-muted-foreground">
                    This privacy policy is governed by the laws of India, including the Information Technology Act, 2000 and applicable rules thereunder.
                  </p>
                </div>
              </section>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
